import { buildTrboRequestUrl } from '../Shared/Trbo/TrboRequestBuilder';
import { mapTrboResponseToProduktkarussellModel } from '../Shared/Trbo/TrboResponseMapper';
import { ITrboResonse } from '../Checkout/Models/Trbo/ITrboResonse';
import { ProduktkarussellItem } from '../Produktdetails/Models/Produktkarussell';
import { isSafeUrl } from '../Shared/UrlSafety';

declare var __ucCmp: any;

const SEARCH_INPUT_SEL = '.mainSearchInput';
const AUTOSUGGEST_SEL = '.autoSuggest';
const DEFAULT_BLOCK_SEL = '.defaultSuggest';
const PRODUCTS_COL_SEL = '[data-default-suggest-products]';
const SPINNER_SEL = '[data-default-suggest-spinner]';
const BODY_OPEN_CLASS = 'search-suggest-open';
const SUGGEST_OPEN_CLASS = 'autoSuggest-container';
const DEFAULT_TRBO_SLOT = 'Product_Reco_Suggest_s2s';
const FALLBACK_API_URL = '/umbraco/api/DefaultSuggestApi/GetFallbackProducts';
const MAX_PRODUCTS = 3;

const TRBO_CONSENT_ID = 'ryxCCtrEX';
const GTM_CONSENT_ID = 'BJ59EidsWQ';

interface DefaultSuggestProduct {
    id: string;
    title: string;
    author: string;
    auflage: string;
    imageUrl: string;
    deeplink: string;
}

let productsLoaded = false;
let productsInFlight = false;

export function openDefaultSuggest(input: HTMLInputElement): void {
    const defaultBlocks = document.querySelectorAll<HTMLElement>(DEFAULT_BLOCK_SEL);
    if (defaultBlocks.length === 0) {
        return;
    }

    // Hide live-suggest siblings (direct children of .autoSuggest) so leftovers
    // from a previous typing session don't bleed through under the default block.
    document.querySelectorAll<HTMLElement>(AUTOSUGGEST_SEL).forEach(autoSuggest => {
        autoSuggest
            .querySelectorAll<HTMLElement>(
                ':scope > .Titelvorschlaege, :scope > .category-suggestions, :scope > .results'
            )
            .forEach(element => {
                element.style.display = 'none';
            });
        autoSuggest.classList.add(SUGGEST_OPEN_CLASS);
    });

    defaultBlocks.forEach(block => {
        block.style.display = '';
    });

    document.body.classList.add(BODY_OPEN_CLASS);
    input.setAttribute('aria-expanded', 'true');

    if (!productsLoaded && !productsInFlight) {
        productsInFlight = true;
        const slot = defaultBlocks[0].dataset.trboSlot || DEFAULT_TRBO_SLOT;
        loadProducts(slot)
            .then(products => {
                if (products.length > 0) {
                    renderProducts(products);
                } else {
                    hideProductsColumn();
                }
                productsLoaded = true;
            })
            .catch(() => {
                // Permanent failure (CMP rejection, fallback API consistently
                // 500). Hide the column and mark loaded so we don't re-enter
                // the polling+fetch loop on every refocus for the rest of the
                // session.
                hideProductsColumn();
                productsLoaded = true;
            })
            .finally(() => {
                productsInFlight = false;
            });
    }
}

export function closeSuggest(): void {
    document.querySelectorAll<HTMLElement>(AUTOSUGGEST_SEL).forEach(autoSuggest => {
        autoSuggest.classList.remove(SUGGEST_OPEN_CLASS);
    });

    document.querySelectorAll<HTMLElement>(DEFAULT_BLOCK_SEL).forEach(block => {
        block.style.display = 'none';
    });

    document.body.classList.remove(BODY_OPEN_CLASS);

    document.querySelectorAll<HTMLInputElement>(SEARCH_INPUT_SEL).forEach(input => {
        input.setAttribute('aria-expanded', 'false');
        input.removeAttribute('aria-activedescendant');
    });
}

async function loadProducts(slot: string): Promise<DefaultSuggestProduct[]> {
    const trboAllowed = await isTrboAllowed();
    if (trboAllowed) {
        const trboItems = await tryLoadTrbo(slot);
        if (trboItems.length > 0) {
            return trboItems.slice(0, MAX_PRODUCTS).map(mapTrboItem);
        }
    }
    const apiProducts = await loadFallbackFromApi();
    return apiProducts.slice(0, MAX_PRODUCTS);
}

async function isTrboAllowed(): Promise<boolean> {
    // By the time the user focuses the search, the CMP has either loaded
    // or it hasn't — there's no value in polling. If it's missing or not
    // initialised yet, treat that as "no consent" and let the caller fall
    // through to the fallback API.
    try {
        if (window['__ucCmp'] === undefined || (await __ucCmp.isInitialized()) === false) {
            return false;
        }
        const details = await __ucCmp.getConsentDetails();
        const trboGiven = details.services[TRBO_CONSENT_ID]?.consent.given;
        const gtmGiven = details.services[GTM_CONSENT_ID]?.consent.given;
        return Boolean(trboGiven && gtmGiven);
    } catch {
        return false;
    }
}

async function tryLoadTrbo(slot: string): Promise<ProduktkarussellItem[]> {
    if (window['_trbo'] === undefined) {
        return [];
    }

    let response: Response;
    try {
        response = await fetch(encodeURI(buildTrboRequestUrl(slot)));
    } catch {
        return [];
    }
    if (!response.ok) {
        return [];
    }

    let data: ITrboResonse;
    try {
        data = (await response.json()) as ITrboResonse;
    } catch {
        return [];
    }

    const mapped = mapTrboResponseToProduktkarussellModel(data);
    return mapped.karussellItems ?? [];
}

async function loadFallbackFromApi(): Promise<DefaultSuggestProduct[]> {
    let response: Response;
    try {
        response = await fetch(FALLBACK_API_URL, { credentials: 'same-origin' });
    } catch {
        return [];
    }
    if (!response.ok) {
        return [];
    }

    try {
        const data = (await response.json()) as DefaultSuggestProduct[] | null;
        return Array.isArray(data) ? data : [];
    } catch {
        return [];
    }
}

function mapTrboItem(item: ProduktkarussellItem): DefaultSuggestProduct {
    return {
        id: String(item.productId ?? ''),
        title: item.title ?? '',
        author: item.authorstring ?? '',
        auflage: item.auflage ?? '',
        imageUrl: item.imageUrl ?? '',
        deeplink: item.productDetailUrl ?? '',
    };
}

function renderProducts(products: DefaultSuggestProduct[]): void {
    // The header partial is rendered multiple times per page (desktop + mobile +
    // tablet variants), so iterate per block to refill every visible block.
    document.querySelectorAll<HTMLElement>(DEFAULT_BLOCK_SEL).forEach(block => {
        const column = block.querySelector<HTMLElement>(PRODUCTS_COL_SEL);
        if (!column) {
            return;
        }
        const list = column.querySelector<HTMLElement>('.suggestItems');
        if (!list) {
            return;
        }
        // Clears the spinner placeholder along with any prior content.
        list.replaceChildren(...products.map(buildProductAnchor));
    });
}

// trbo returns title/author/auflage with HTML entities (e.g. `&Uuml;`).
// `textContent` would render those literally, so decode via the textarea
// trick — safe because textarea content is parsed for entities but never
// executed, and the result still flows into `textContent`.
function decodeHtmlEntities(text: string): string {
    if (!text || text.indexOf('&') === -1) {
        return text;
    }
    const ta = document.createElement('textarea');
    ta.innerHTML = text;
    return ta.value;
}

function buildProductAnchor(product: DefaultSuggestProduct): HTMLAnchorElement {
    const decodedTitle = decodeHtmlEntities(product.title || '');
    const decodedAuthor = decodeHtmlEntities(product.author || '');
    const decodedAuflage = decodeHtmlEntities(product.auflage || '');

    const anchor = document.createElement('a');
    anchor.dataset.area = 'Titelvorschläge';
    anchor.className = 'suggest-tracking item Titelvorschlag';
    anchor.setAttribute('role', 'option');
    // Combobox pattern: items are not in the tab sequence; ↓/↑ navigate via
    // aria-activedescendant, Tab moves focus past the input to the search button.
    anchor.tabIndex = -1;
    if (product.deeplink && isSafeUrl(product.deeplink)) {
        anchor.href = product.deeplink;
    } else {
        anchor.href = '#';
    }

    const imgWrapper = document.createElement('div');
    imgWrapper.className = 'imgWrapper';
    const img = document.createElement('img');
    img.alt = decodedTitle;
    if (product.imageUrl && isSafeUrl(product.imageUrl)) {
        img.src = product.imageUrl;
    }
    imgWrapper.appendChild(img);

    const itemContent = document.createElement('div');
    itemContent.className = 'item-content';

    const author = document.createElement('span');
    author.className = 'author contentEl';
    author.textContent = decodedAuthor;

    const title = document.createElement('span');
    title.className = 'title contentEl';
    title.textContent = decodedTitle;

    const volume = document.createElement('span');
    volume.className = 'volume contentEl';
    volume.textContent = decodedAuflage;

    itemContent.append(author, title, volume);
    anchor.append(imgWrapper, itemContent);
    return anchor;
}

function hideProductsColumn(): void {
    document.querySelectorAll<HTMLElement>(DEFAULT_BLOCK_SEL).forEach(block => {
        const column = block.querySelector<HTMLElement>(PRODUCTS_COL_SEL);
        if (column) {
            column.style.display = 'none';
        }
        // Spinner stays inside the hidden column — no extra cleanup needed,
        // but drop it explicitly so screen readers stop announcing the live
        // region while no products are shown.
        const spinner = block.querySelector<HTMLElement>(SPINNER_SEL);
        spinner?.remove();
    });
}

function init(): void {
    const inputs = document.querySelectorAll<HTMLInputElement>(SEARCH_INPUT_SEL);
    inputs.forEach(input => {
        input.addEventListener('focus', () => {
            if (input.value.trim() === '') {
                openDefaultSuggest(input);
                return;
            }
            // Refocus with existing text: closeSuggest stripped the visibility
            // classes but the live-suggest results from the previous typing are
            // still in the DOM. Restore the container + body class so the panel
            // and backdrop reappear without re-fetching from FactFinder.
            document.querySelectorAll<HTMLElement>(AUTOSUGGEST_SEL).forEach(autoSuggest => {
                autoSuggest.classList.add(SUGGEST_OPEN_CLASS);
            });
            document.body.classList.add(BODY_OPEN_CLASS);
            input.setAttribute('aria-expanded', 'true');
        });
        // Combobox pattern: close when focus leaves the input/suggest area.
        // Listen on `.inputEls` (not the input itself) so focusout bubbles
        // through every intermediate hop — input → erase icon → search button
        // — and we only close once focus actually leaves the wrapper. Listening
        // on the input alone misses the erase-icon → search-button transition,
        // because by then the input has long since lost focus.
        const inputEls = input.closest<HTMLElement>('.inputEls');
        // In the redesign header the .autoSuggest panel is rendered *inside*
        // .inputEls, but in the legacy header it's a sibling within the same
        // <form>. Clicking a suggest link moves focus to that link, firing
        // focusout on the input. If the guard only checks .inputEls, the legacy
        // layout closes the panel on the click's mousedown — before the link can
        // navigate — so the click appears to do nothing. Treat focus moving into
        // the associated suggest panel as "stay open" for both layouts.
        const suggestPanel =
            input.closest<HTMLElement>('form')?.querySelector<HTMLElement>(AUTOSUGGEST_SEL) ??
            inputEls?.querySelector<HTMLElement>(AUTOSUGGEST_SEL) ??
            null;
        inputEls?.addEventListener('focusout', event => {
            const next = event.relatedTarget as Element | null;
            if (next && (inputEls.contains(next) || suggestPanel?.contains(next))) {
                return;
            }
            closeSuggest();
        });
        input.addEventListener('input', () => {
            // The empty-input case is handled in the keyup listener below so we
            // run strictly after ffAutosuggest's jQuery input handler removes
            // `autoSuggest-container` — guaranteed by the keyup-after-input order.
            if (input.value.trim() === '') {
                return;
            }
            document.querySelectorAll<HTMLElement>(DEFAULT_BLOCK_SEL).forEach(block => {
                block.style.display = 'none';
            });
            // Re-show the live-suggest wrappers that openDefaultSuggest hid; the
            // live-suggest fill in ffAutosuggest.ts only `.show()`s the inner
            // sub-blocks, so without this the parent .category-suggestions (and
            // siblings) stay hidden and ↓/↑ keyboard nav can't find any items.
            document.querySelectorAll<HTMLElement>(AUTOSUGGEST_SEL).forEach(autoSuggest => {
                autoSuggest
                    .querySelectorAll<HTMLElement>(
                        ':scope > .Titelvorschlaege, :scope > .category-suggestions, :scope > .results'
                    )
                    .forEach(element => {
                        element.style.display = '';
                    });
            });
        });
        input.addEventListener('keydown', event => {
            if (event.key === 'Escape') {
                closeSuggest();
            }
        });

        // keyup fires after the input event in the standard event sequence,
        // so by this point ffAutosuggest's jQuery input handler has already
        // removed `autoSuggest-container`. Reopening here re-adds it without a
        // race. Covers backspace, delete and cut (Ctrl+X) — anything that ends
        // with the input empty after a key press.
        input.addEventListener('keyup', () => {
            if (input.value.trim() === '') {
                openDefaultSuggest(input);
            }
        });

        // Clicking the X icon clears `input.value` programmatically, which does
        // NOT fire the input event. Re-open the default-suggest explicitly so
        // the empty-state UX matches focus + backspace-to-empty.
        const eraseIcons = input
            .closest('.inputEls')
            ?.querySelectorAll<HTMLElement>('.eraseSearchTerm');
        eraseIcons?.forEach(icon => {
            icon.addEventListener('click', () => {
                queueMicrotask(() => openDefaultSuggest(input));
            });
        });
    });
}

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
} else {
    init();
}
